//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { entityModalFactory } from 'common/entity/generic/EntityModal';

import { translate } from 'react-i18next';
import theme from './SlotsSelectorModal.scss';
import { SlotType } from 'common/enums/SlotEnum';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import _ from 'lodash';
import moment from 'moment-timezone';
import { AddressLatLng } from 'common/models/internal/AddressLatLng';

type Props = {
    className?: string,
    title: string,
    theme: Object,
    defaultSlots?: Array<{ dayOfWeek: string } | any>,
    onDismiss: (?boolean) => void,
    onSlotApply: Function,
    getUpdatedTimeSlots: Function,
    deriveSlotType: Function,
    deriveSelectedDays: Function,
    createInitialSlots: Function,
    addSlot: Function,
    deleteSingleSlot: Function,
    copySlotToAll: Function,
    validateTimeSlot: Function,
    syncSlots?: Function
};

type State = {
    errorMessage?: ?string,
    type: string,
    timeSlots: Array<any>,
    selectedDays: Array<string>
};

const slotSelectorModalFactory = ({ SlotSelectorSection }) => {
    const SlotSelectorModal = entityModalFactory({
        InnerContent: SlotSelectorSection
    });

    class SlotsSelectorModal extends React.Component<Props, State> {
        // getDerivedStateFromProps candidate
        UNSAFE_componentWillMount = () => {
            const {
                defaultSlots,
                deriveSlotType,
                deriveSelectedDays,
                createInitialSlots
            } = this.props;
            this.setState({
                type: defaultSlots
                    ? deriveSlotType(defaultSlots)
                    : SlotType.EVERYDAY,
                timeSlots: createInitialSlots(defaultSlots),
                selectedDays: deriveSelectedDays(defaultSlots)
            });
        };

        UNSAFE_componentWillReceiveProps = (nextProps: Props) => {
            if (this.props.defaultSlots !== nextProps.defaultSlots) {
                const {
                    defaultSlots,
                    deriveSlotType,
                    createInitialSlots
                } = nextProps;
                this.setState({
                    type: defaultSlots
                        ? deriveSlotType(defaultSlots)
                        : SlotType.EVERYDAY,
                    timeSlots: defaultSlots || createInitialSlots()
                });
            }
        };

        updateSelectedDays = selectedDays => {
            this.setState({
                selectedDays
            });
        };

        handleStartLocationChange = ({
            lat,
            lng,
            desc,
            slotFor,
            deleteLocation
        }) => {
            const startLocation = deleteLocation
                ? AddressLatLng.fromJS({
                      latLng: { lat, lng, accuracy: 0 },
                      address: desc
                  })
                : null;
            this.handleSingleLocationChange('START', startLocation, slotFor);
        };

        handleSingleSlotChange = (timeSlotData: {
            slotFor: string,
            index: number,
            start: moment,
            end: moment,
            startLocation?: AddressLatLng,
            endLocation?: AddressLatLng,
            deleteStartLocation?: boolean,
            deleteEndLocation?: boolean
        }) => {
            const { slotFor } = timeSlotData;
            const { getUpdatedTimeSlots } = this.props;
            const daysList = Object.values(DayOfWeekType);
            if (slotFor === SlotType.EVERYDAY) {
                // Recursively update for each day
                daysList.forEach(slotDay => {
                    this.handleSingleSlotChange({
                        ...timeSlotData,
                        slotFor: slotDay
                    });
                });
                return;
            }
            if (daysList.indexOf(slotFor) >= 0) {
                this.setState(state => {
                    const { timeSlots } = state;
                    const updatedTimeSlots = getUpdatedTimeSlots({
                        ...timeSlotData,
                        timeSlots
                    });
                    return {
                        timeSlots: updatedTimeSlots
                    };
                });
                return;
            }
            throw new Error(`Unknown slotType: ${slotFor}`);
        };

        handleSingleSlotDelete = ({
            slotFor,
            index
        }: {
            slotFor: string,
            index: number
        }) => {
            const { deleteSingleSlot } = this.props;
            if (slotFor === SlotType.EVERYDAY) {
                // Recursively update for each day
                Object.values(DayOfWeekType).forEach(slotDay => {
                    this.handleSingleSlotDelete({ slotFor: slotDay, index });
                });
                return;
            }
            if (Object.values(DayOfWeekType).indexOf(slotFor) >= 0) {
                this.setState(state => {
                    const { timeSlots } = state;
                    const updatedTimeSlots = deleteSingleSlot({
                        timeSlots,
                        slotFor,
                        index
                    });
                    return {
                        timeSlots: updatedTimeSlots
                    };
                });
            }
        };

        onSlotTypeChange = value => {
            const { syncSlots } = this.props;
            const { timeSlots } = this.state;
            const slotType = value.value;
            const updatedSlots = syncSlots?.({
                timeSlots,
                slotType
            });
            this.setState({
                type: slotType,
                timeSlots: updatedSlots || timeSlots
            });
        };

        onCopySlotToAll = (slotFor: string) => {
            const { copySlotToAll } = this.props;
            this.setState(state => {
                const { timeSlots } = state;
                const updatedTimeSlots = copySlotToAll({
                    timeSlots,
                    slotFor
                });
                return { timeSlots: updatedTimeSlots };
            });
        };

        onAddSlot = (slotFor: string) => {
            const { addSlot } = this.props;
            if (slotFor === SlotType.EVERYDAY) {
                Object.values(DayOfWeekType).forEach(slotDay => {
                    this.onAddSlot(slotDay);
                });
                return;
            }
            this.setState(state => {
                const { timeSlots } = state;
                const updatedTimeSlots = addSlot({
                    timeSlots,
                    slotFor
                });
                return { timeSlots: updatedTimeSlots };
            });
        };

        handleApplyClick = () => {
            const { onSlotApply, onDismiss } = this.props;
            const errorMessage = this.doFormValidation();
            if (errorMessage) {
                this.setState(
                    {
                        errorMessage
                    },
                    this.clearErrorMessage
                );
                return;
            }
            onSlotApply(this.getApplicableSlots());
            onDismiss(true);
        };

        getApplicableSlots = () => {
            const { type, timeSlots, selectedDays } = this.state;
            switch (type) {
                case SlotType.EVERYDAY: {
                    // For everyday use monday's slot
                    const [firstDay] = timeSlots;
                    return Object.values(DayOfWeekType).map(day => {
                        return firstDay.setDayOfWeek(day);
                    });
                }
                case SlotType.CUSTOM: {
                    return timeSlots.filter(
                        timeSlot =>
                            selectedDays.indexOf(timeSlot.dayOfWeek) >= 0
                    );
                }
                default:
                    return [];
            }
        };

        hasValidSlots = () => {
            const { validateTimeSlot } = this.props;
            const timeSlots = this.getApplicableSlots();
            return _.every(timeSlots, timeSlot => {
                return validateTimeSlot(timeSlot);
            });
        };

        clearErrorMessage = () => {
            // Clear message after 5 sec
            setTimeout(() => {
                this.setState({
                    errorMessage: null
                });
            }, 5000);
        };

        doFormValidation = () => {
            if (!this.hasValidSlots()) {
                return this.props.t('slotSelector.invalidTimeSlots');
            }
        };

        render() {
            const {
                className,
                theme,
                onDismiss,
                title,
                addSlot,
                ...others
            } = this.props;
            const { errorMessage, timeSlots, type, selectedDays } = this.state;
            return (
                <SlotSelectorModal
                    {...others}
                    theme={theme}
                    title={title}
                    className={classnames(className, theme.slotModalOverlay)}
                    onAccept={this.handleApplyClick}
                    onDismiss={onDismiss}
                    acceptErrorMessage={errorMessage}
                    timeSlots={timeSlots}
                    type={type}
                    selectedDays={selectedDays}
                    onAddSlot={addSlot && this.onAddSlot}
                    onCopySlotToAll={this.onCopySlotToAll}
                    updateSelectedDays={this.updateSelectedDays}
                    onSingleSlotChange={this.handleSingleSlotChange}
                    onSingleSlotDelete={this.handleSingleSlotDelete}
                    onSlotTypeChange={this.onSlotTypeChange}
                />
            );
        }
    }
    return translate('entityManagement')(
        themr('SlotSelectorModal', theme)(SlotsSelectorModal)
    );
};

export default slotSelectorModalFactory;
